import { useCallback, useContext, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { string } from "yup";
import { TextField } from "../../../../../components/forms/text";
import { ConfigContext } from "../../../../../pages/token-onboarding/single/config-context";
import { Field } from "../../internal/field";
import { FieldHook, RenderFuncProps } from "../common";

const Elem = ({ item }: RenderFuncProps) => {
  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);
  const { config } = useContext(ConfigContext);
  const { control } = useFormContext();

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: item.field,
  });

  // Make sure user cannot type more than 4 characters
  const onChange = useCallback(
    (e: any) => {
      if (
        !field.value ||
        field.value.length < 4 ||
        e.target.value?.length < field.value?.length
      ) {
        field.onChange(e.target.value);
      }
    },
    [field.value, field.onChange]
  );

  return (
    <>
      <div>{t("application-pin")}</div>
      <div>
        <TextField
          {...field}
          onChange={onChange}
          fullWidth
          type="text"
          label=""
          error={!!error}
          helperText={error?.message}
          placeholder={t("create-a-4-digit-pin-number")}
        />

        <small>
          <em>
            {t("this-will-be-included-in-email", {
              launchName: config.configuration.launchName?.[currentLanguage],
            })}
          </em>
        </small>
      </div>
    </>
  );
};

export const useUserApplicationPinField: FieldHook = () => {
  const { t } = useTranslation("fields");
  const ERROR_TEXT = useMemo(() => t("pin-must-be-4-digits"), [t]);
  const schema = useMemo(
    () =>
      string()
        .max(4, ERROR_TEXT)
        .min(4, ERROR_TEXT)
        .matches(/[0-9]+/)
        .label(t("application-pin"))
        .required(
          t("required-field", {
            ns: "errors",
            field: t("application-pin"),
          })
        ),
    [t, ERROR_TEXT]
  );

  return useMemo(
    () => ({
      schema,
      elem: Elem,
    }),
    [schema]
  );
};
